import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { formatPrice } from '@rcg/pricing-format';
import Button from '../../UiComponents/Button';
import useDictionary from '../../../hooks/useDictionary';
import Dictionary from '../../../utils/dictionary';

const useStyles = makeStyles((Theme) => ({
  cta: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    alignSelf: 'stretch',
    gap: '8px',
    margin: 'auto 0',
    [Theme.breakpoints.down('sm')]: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      margin: '12px 0 0 0',
    },
  },
  ctaLeft: {
    display: 'none',
    [Theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '4px',
      flex: 1,
    },
  },
  payBadgeMobile: {
    display: 'none',
    [Theme.breakpoints.down('sm')]: {
      display: 'inline-flex',
      alignItems: 'center',
      borderRadius: '12px',
      padding: '2px 10px',
      fontSize: Theme.typography.size[1],
      fontWeight: 600,
      whiteSpace: 'nowrap',
    },
  },
  payBadgeMobileNow: {
    backgroundColor: '#E9F6F0',
    color: '#33734F',
    border: '1px solid #BCE2D2',
  },
  payBadgeMobileLater: {
    backgroundColor: '#439465',
    color: '#FFFFFF',
    border: '1px solid #439465',
  },
  payLabelMobile: {
    display: 'none',
    [Theme.breakpoints.down('sm')]: {
      display: 'inline-block',
      fontSize: Theme.typography.size[1],
      fontWeight: 700,
      padding: '3px 14px',
      whiteSpace: 'nowrap',
      backgroundColor: '#E8F3F4',
      clipPath: 'polygon(8px 0, 100% 0, calc(100% - 8px) 100%, 0 100%)',
    },
  },
  payLabelMobileNow: {
    color: '#4C200B',
  },
  payLabelMobileLater: {
    color: '#151617',
  },
  priceContainer: {
    textAlign: 'right',
    [Theme.breakpoints.down('sm')]: {
      textAlign: 'left',
    },
  },
  priceContainerDesktop: {
    [Theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  price: {
    fontSize: Theme.typography.size[6],
    fontWeight: 600,
    color: Theme.palette.gray[900] || '#212121',
  },
  wasPrice: {
    fontSize: Theme.typography.size[2],
    color: Theme.palette.gray[500],
    textDecoration: 'line-through',
    display: 'block',
  },
  ctaRight: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    width: '100%',
    [Theme.breakpoints.down('sm')]: {
      // Stack seats-left above the Select button on mobile.
      flexDirection: 'column-reverse',
      flex: '0 0 45%',
      width: 'auto',
    },
  },
  selectBtn: {
    width: '100%',
    borderRadius: '4px',
    flexShrink: 0,
    '& > span': {
      fontFamily: Theme.typography.base.regular,
    },
  },
  seatsLeft: {
    color: '#FF434E',
    fontSize: Theme.typography.size[1],
    lineHeight: '150%',
    textAlign: 'center',
    width: '100%',
    margin: 0,
    whiteSpace: 'nowrap',
  },
}));

const PriceDisplay = ({ classes, pricePerPassenger, wasPricePerPassenger, currency }) => (
  <div className={classes.priceContainer}>
    <span className={classes.price}>
      {formatPrice(pricePerPassenger.value, currency, true, true, false)}
    </span>
    {wasPricePerPassenger?.value && (
      <span className={classes.wasPrice}>
        {formatPrice(wasPricePerPassenger.value, currency, true, true, false)}
      </span>
    )}
  </div>
);

const FareCardCTA = ({ fare, isSelected, onSelect, paymentLabel, isCelebrity, payNow }) => {
  const classes = useStyles();
  const getDictionary = useDictionary(Dictionary);
  const { pricePerPassenger, wasPricePerPassenger, isPaymentRequired, seatsAvailable } = fare;
  // The parent already folds the final-payment window into the label — the badge color must
  // agree with it, not re-derive from the fare alone.
  const paymentIsDueNow = payNow ?? isPaymentRequired;
  const currency = fare.currency || pricePerPassenger.currency;

  const showSeatsLeft = typeof seatsAvailable === 'number' && seatsAvailable <= 8;
  const seatsLeftLabel =
    seatsAvailable === 1
      ? getDictionary(Dictionary.airSeatLeftLabel.key)
      : getDictionary(Dictionary.airSeatsLeftLabel.key);

  const paymentBadgeClass = isCelebrity
    ? `${classes.payLabelMobile} ${
        paymentIsDueNow ? classes.payLabelMobileNow : classes.payLabelMobileLater
      }`
    : `${classes.payBadgeMobile} ${
        paymentIsDueNow ? classes.payBadgeMobileNow : classes.payBadgeMobileLater
      }`;

  return (
    <div className={classes.cta}>
      <div className={classes.ctaLeft}>
        {paymentLabel && <span className={paymentBadgeClass}>{paymentLabel}</span>}
        <PriceDisplay
          classes={classes}
          pricePerPassenger={pricePerPassenger}
          wasPricePerPassenger={wasPricePerPassenger}
          currency={currency}
        />
      </div>
      <div className={classes.priceContainerDesktop}>
        <PriceDisplay
          classes={classes}
          pricePerPassenger={pricePerPassenger}
          wasPricePerPassenger={wasPricePerPassenger}
          currency={currency}
        />
      </div>
      <div className={classes.ctaRight}>
        <Button
          data-testid="fare-select-btn"
          className={classes.selectBtn}
          variant="contained"
          color="primary"
          onClick={onSelect}>
          {isSelected
            ? getDictionary(Dictionary.airFareSelectedLabel.key)
            : getDictionary(Dictionary.airFareSelectLabel.key)}
        </Button>
        {showSeatsLeft && (
          <p className={classes.seatsLeft}>
            {seatsAvailable}
            {seatsLeftLabel}
          </p>
        )}
      </div>
    </div>
  );
};

export default FareCardCTA;
